import { useState } from "preact/compat"
import { IntegrationWithJobInfo, updateIntegration } from "@/api/integration"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast"
import { HttpError, HttpResponse } from "@/api/client"
import { Result, useAsyncEffect } from "@/utils"

const NONE_VALUE = "__none__"

export interface CreditMemoLocation {
  id: string
  name?: string | null
}

interface CreditMemoLocationSettingsProps {
  integration: IntegrationWithJobInfo
  /** Explains when the location is required for this provider. */
  description: string
  /** Label for the unset option, e.g. "None (single-entity company)". */
  none_label: string
  fetch_locations: (integrationId: string) => Promise<Result<HttpResponse<CreditMemoLocation[]>, HttpError>>
}

/**
 * Shared credit-memo location picker: loads the provider's locations, lets the user pick the one
 * stamped on credit memos created when applying cash, and saves it to the integration config as
 * `credit_memo_location_id`. Used by the Sage Intacct and NetSuite settings pages.
 */
export function CreditMemoLocationSettings({ integration, description, none_label, fetch_locations }: CreditMemoLocationSettingsProps) {
  const config = integration.data as { credit_memo_location_id?: string | null }
  const [locations, setLocations] = useState<CreditMemoLocation[]>([])
  const [selected, setSelected] = useState<string | null>(config.credit_memo_location_id ?? null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useAsyncEffect(async () => {
    const res = await fetch_locations(integration.id)
    if (res.ok) {
      setLocations(res.value.data)
    }
    setLoading(false)
  }, [integration.id])

  async function handleSave() {
    setSaving(true)
    const res = await updateIntegration(integration.id, { data: { credit_memo_location_id: selected } })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Save failed", description: "Could not save the credit-memo location.", variant: "destructive" })
      return
    }
    toast({ title: "Settings saved", description: "Credit-memo location updated." })
  }

  if (loading) {
    return <div class="text-gray-500 text-sm">Loading locations…</div>
  }

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">{description}</p>
      <div class="space-y-1 max-w-md">
        <Label>Credit-memo location</Label>
        <Select
          value={selected ?? NONE_VALUE}
          onValueChange={(v: string) => setSelected(v === NONE_VALUE ? null : v)}>
          <SelectTrigger>
            <SelectValue placeholder="Select location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>{none_label}</SelectItem>
            {locations.map((loc: CreditMemoLocation) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name ? `${loc.id} — ${loc.name}` : loc.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" disabled={saving} onClick={handleSave}>
        {saving ? "Saving…" : "Save location"}
      </Button>
    </div>
  )
}
